import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Tenants from './Tenants';
import TenantDetails from './TenantDetails';
import TenantCreate, { CreateTenantRequest } from './TenantCreate';
import DetailsEditor from './DetailsEditor';
import { useTenants } from '../hooks/useTenants';
import { useApiClient } from '../hooks/useApiClient';
import './TenantsPage.scss';

const TenantsPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetch } = useTenants();
  const apiClient = useApiClient();
  const [selectedTenant, setSelectedTenant] = useState<string | null>(tenantId || null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [tenantData, setTenantData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleEditorAction = () => {
    setSelectedTenant(null);
    setShowCreateForm(false);
    refetch();
    navigate('/tenants');
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    refetch();
    navigate('/tenants');
  };

  const fetchTenantDetails = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/tenants/${id}`);

      if (response.ok) {
        const tenant = await response.json();
        console.log('TenantsPage - Fetched tenant data:', tenant);
        setTenantData(tenant);
        return tenant;
      } else {
        throw new Error('Failed to load tenant details');
      }
    } catch (err) {
      console.error('Error loading tenant:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const handleUpdateTenant = async (id: string, data: { name: string; email: string }) => {
    try {
      const response = await apiClient.put(`/tenants/${id}`, {
        name: data.name.trim(),
        email: data.email.trim(),
        status: tenantData?.status
      });

      if (response.ok) {
        const updatedTenant = await response.json();
        setTenantData(updatedTenant);
        handleEditorAction();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tenant');
      }
    } catch (err) {
      console.error('Error updating tenant:', err);
      throw err;
    }
  };

  const handleDeleteTenant = async (id: string) => {
    try {
      const response = await apiClient.delete(`/tenants/${id}`);

      if (response.ok) {
        handleEditorAction();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete tenant');
      }
    } catch (err) {
      console.error('Error deleting tenant:', err);
      throw err;
    }
  };

  const handleSaveTenant = async (data: CreateTenantRequest) => {
    try {
      const response = await apiClient.post('/tenants', {
        name: data.name.trim(),
        email: data.email.trim()
      });

      if (response.ok) {
        handleCreateSuccess();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create tenant');
      }
    } catch (err) {
      console.error('Error creating tenant:', err);
      throw err;
    }
  };

  const handleCreateTenant = () => {
    setSelectedTenant(null);
    setShowCreateForm(true);
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (tenantId) {
      console.log('TenantsPage - Setting selected tenant:', tenantId);
      setSelectedTenant(tenantId);
      setShowCreateForm(false);
      fetchTenantDetails(tenantId);
    } else if (location.pathname === '/tenants') {
      setSelectedTenant(null);
      setShowCreateForm(false);
      setTenantData(null);
    }
  }, [tenantId, location.pathname, fetchTenantDetails]);

  if (isMobile && (selectedTenant || showCreateForm)) {
    return (
      <div className="tenants-page__mobile-container">
        <DetailsEditor
          title={selectedTenant ? "Tenant Details" : "Create Tenant"}
          showSave={!!selectedTenant || showCreateForm}
          showDelete={!!selectedTenant}
          onComplete={(action) => {
            if (action === 'save') {
              handleCreateSuccess();
            } else {
              handleEditorAction();
            }
          }}
        >
          {selectedTenant ? (
            <TenantDetails 
              tenant={tenantData}
              loading={loading}
              onSave={(data: { name: string; email: string }) => handleUpdateTenant(selectedTenant, data)}
              onDelete={() => handleDeleteTenant(selectedTenant)}
            />
          ) : (
            <TenantCreate onSave={handleSaveTenant} />
          )}
        </DetailsEditor>
      </div>
    );
  }

  return (
    <div className="tenants-page">
      <Tenants onTenantSelect={setSelectedTenant} onCreateTenant={handleCreateTenant} />
      {(selectedTenant || showCreateForm) && (
        <div className="tenants-page__overlay">
          <DetailsEditor
            title={selectedTenant ? "Tenant Details" : "Create Tenant"}
            showSave={!!selectedTenant || showCreateForm}
            showDelete={!!selectedTenant}
            onComplete={(action) => {
              if (action === 'save') {
                handleCreateSuccess();
              } else {
                setSelectedTenant(null);
                setShowCreateForm(false);
                refetch();
                navigate('/tenants');
              }
            }}
          >
            {selectedTenant ? (
              <TenantDetails 
                tenant={tenantData}
                loading={loading}
                onSave={(data: { name: string; email: string }) => handleUpdateTenant(selectedTenant, data)}
                onDelete={() => handleDeleteTenant(selectedTenant)}
              />
            ) : (
              <TenantCreate onSave={handleSaveTenant} />
            )}
          </DetailsEditor>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;